import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { formatCurrency, today, isAdminUser } from '../utils/helpers';

const emptyItem = { product_id: '', quantity: 1, rate: 0, tax_rate: 0 };

const emptyPurchase = {
  ledger_id: '',
  purchase_date: today(),
  vehicle_id: '',
  vehicle_text: '',
  items: [{ ...emptyItem }],
  discount: 0,
  transport_charges: 0,
  paid_amount: 0,
  payment_mode: 'credit',
  notes: '',
};

const emptySale = {
  ledger_id: '',
  sale_date: today(),
  vehicle_id: '',
  vehicle_text: '',
  delivery_address: '',
  items: [{ ...emptyItem }],
  discount: 0,
  transport_charges: 0,
  paid_amount: 0,
  payment_mode: 'credit',
  delivery_status: 'pending',
  notes: '',
};

function calcTotal(form) {
  let sub = 0;
  let tax = 0;
  (form.items || []).forEach((it) => {
    const line = parseFloat(it.quantity || 0) * parseFloat(it.rate || 0);
    sub += line;
    tax += line * (parseFloat(it.tax_rate || 0) / 100);
  });
  const total =
    sub +
    tax +
    parseFloat(form.transport_charges || 0) -
    parseFloat(form.discount || 0);
  return { sub, tax, total };
}

export default function TradeEntry() {
  const nav = useNavigate();

  const [customerLedgers, setCustomerLedgers] = useState([]);
  const [supplierLedgers, setSupplierLedgers] = useState([]);
  const [products, setProducts] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [includePurchase, setIncludePurchase] = useState(true);
  const [includeSale, setIncludeSale] = useState(true);
  const [saving, setSaving] = useState(false);

  const [purchaseForm, setPurchaseForm] = useState(emptyPurchase);
  const [saleForm, setSaleForm] = useState(emptySale);

  useEffect(() => {
    api
      .get('/ledgers?type=customer')
      .then((r) =>
        setCustomerLedgers((r.data.data || []).filter((c) => c.status == 1)),
      );
    api
      .get('/ledgers?type=supplier')
      .then((r) =>
        setSupplierLedgers((r.data.data || []).filter((s) => s.status == 1)),
      );
    api
      .get('/products')
      .then((r) => setProducts((r.data.data || []).filter((p) => p.status == 1)));
    api.get('/vehicles').then((r) => setVehicles(r.data.data || []));
  }, []);

  const purchaseTotals = useMemo(
    () => calcTotal(purchaseForm),
    [purchaseForm],
  );
  const saleTotals = useMemo(() => calcTotal(saleForm), [saleForm]);

  const purchasableProductIds = useMemo(() => {
    const ids = new Set();
    (purchaseForm.items || []).forEach((it) => {
      if (it?.product_id) ids.add(String(it.product_id));
    });
    return ids;
  }, [purchaseForm.items]);

  const saleProductOptions = useMemo(() => {
    // Keep the existing behavior: sales items can only be selected from the items added in the Purchase section.
    if (!purchasableProductIds.size) return [];
    return products.filter((p) => purchasableProductIds.has(String(p.id)));
  }, [products, purchasableProductIds]);

  useEffect(() => {
    // Keep Sale item rows aligned with Purchase rows:
    // - Purchase can "create" (add) items/rows
    // - Sale cannot create new rows; it can only select from purchase-created items
    setSaleForm((prev) => {
      const purchaseLen = (purchaseForm.items || []).length;
      const nextItems = [...(prev.items || [])];
      if (nextItems.length < purchaseLen) {
        while (nextItems.length < purchaseLen) nextItems.push({ ...emptyItem });
      } else if (nextItems.length > purchaseLen) {
        nextItems.length = purchaseLen;
      }
      const sanitized = nextItems.map((it) =>
        it?.product_id && !purchasableProductIds.has(String(it.product_id))
          ? { ...it, product_id: '' }
          : it,
      );
      return { ...prev, items: sanitized.length ? sanitized : [{ ...emptyItem }] };
    });
  }, [purchaseForm.items, purchasableProductIds]);

  const addPurchaseItem = () =>
    setPurchaseForm({
      ...purchaseForm,
      items: [...purchaseForm.items, { ...emptyItem }],
    });
  const removePurchaseItem = (i) =>
    setPurchaseForm({
      ...purchaseForm,
      items: purchaseForm.items.filter((_, ix) => ix !== i),
    });
  const updatePurchaseItem = (i, field, val) => {
    const items = [...purchaseForm.items];
    items[i] = { ...items[i], [field]: val };
    if (field === 'product_id') {
      const p = products.find((x) => x.id == val);
      if (p) {
        items[i].rate = p.purchase_price;
        items[i].tax_rate = p.tax_rate;
      }
    }
    setPurchaseForm({ ...purchaseForm, items });
  };

  const updateSaleItem = (i, field, val) => {
    const items = [...saleForm.items];
    items[i] = { ...items[i], [field]: val };
    if (field === 'product_id') {
      if (val && !purchasableProductIds.has(String(val))) {
        items[i] = { ...items[i], product_id: '' };
        setSaleForm({ ...saleForm, items });
        return toast.error('Only items added in Purchase can be sold here');
      }
      const p = products.find((x) => x.id == val);
      if (p) {
        items[i].rate = p.selling_price;
        items[i].tax_rate = p.tax_rate;
      }
    }
    setSaleForm({ ...saleForm, items });
  };

  const resetForms = () => {
    setIncludePurchase(true);
    setIncludeSale(true);
    setPurchaseForm(emptyPurchase);
    setSaleForm(emptySale);
  };

  const saveBoth = async () => {
    if (!includePurchase && !includeSale)
      return toast.error('Select Purchase and/or Sale');

    if (includePurchase) {
      if (!purchaseForm.ledger_id) return toast.error('Supplier ledger required');
      if (
        !purchaseForm.items.length ||
        purchaseForm.items.some((i) => !i.product_id)
      )
        return toast.error('Add at least one purchase product');
    }
    if (includeSale) {
      if (!saleForm.ledger_id) return toast.error('Customer ledger required');
      if (!saleForm.items.length || saleForm.items.some((i) => !i.product_id))
        return toast.error('Add at least one sale product');
    }

    const resolveVehicleId = (vehicleText) => {
      const txt = String(vehicleText || '').trim().toLowerCase();
      if (!txt) return '';
      const hit = vehicles.find(
        (v) => String(v.vehicle_no || '').trim().toLowerCase() === txt,
      );
      return hit?.id || '';
    };

    const purchasePayload = includePurchase
      ? {
          ...purchaseForm,
          vehicle_id: resolveVehicleId(purchaseForm.vehicle_text),
        }
      : null;
    const salePayload = includeSale
      ? {
          ...saleForm,
          vehicle_id: resolveVehicleId(saleForm.vehicle_text),
        }
      : null;

    setSaving(true);
    try {
      if (includePurchase) await api.post('/purchases', purchasePayload);
      if (includeSale) await api.post('/sales', salePayload);

      toast.success(
        includePurchase && includeSale
          ? 'Purchase & Sale created'
          : includePurchase
            ? 'Purchase created'
            : 'Sale created',
      );
      resetForms();
      nav('/trade');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <h3 style={{ marginRight: 8 }}>Add Sales / Purchase</h3>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Create Purchase, Sale, or both.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-gray" onClick={() => nav('/trade')} type="button" disabled={saving}>
            <ArrowLeft size={14} /> Back
          </button>
          <button className="btn btn-blue" onClick={saveBoth} type="button" disabled={saving}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {!isAdminUser() && (
        <div style={{background:'#fef3c7',border:'1px solid #fcd34d',padding:'10px 14px',margin:'12px',borderRadius:8,fontSize:13,color:'#92400e'}}>
          ⚠ Your entries will be saved as <strong>pending</strong> until an admin approves them. Stock changes, bank balances, and invoice settlements only apply after approval.
        </div>
      )}

      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={includePurchase}
              onChange={(e) => setIncludePurchase(e.target.checked)}
            />
            Include Purchase
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={includeSale}
              onChange={(e) => setIncludeSale(e.target.checked)}
            />
            Include Sale
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 12 }}>
          {/* Purchase */}
          <div style={{ opacity: includePurchase ? 1 : 0.45, pointerEvents: includePurchase ? 'auto' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h4 style={{ margin: '6px 0', fontSize: 13, color: '#1e40af' }}>Purchase</h4>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Total: <strong>{formatCurrency(purchaseTotals.total)}</strong>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Ledger (Supplier) *</label>
                <select
                  value={purchaseForm.ledger_id}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, ledger_id: e.target.value })}
                >
                  <option value="">-- Select --</option>
                  {supplierLedgers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={purchaseForm.purchase_date}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, purchase_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Vehicle (for transport)</label>
                <input
                  value={purchaseForm.vehicle_text}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, vehicle_text: e.target.value })}
                  placeholder="Type vehicle number (optional)"
                />
              </div>
            </div>

            <h4 style={{ margin: '12px 0 8px', fontSize: 13, color: '#475569' }}>Items</h4>
            <table className="items-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Product</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Tax%</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {purchaseForm.items.map((it, i) => {
                  const line = parseFloat(it.quantity || 0) * parseFloat(it.rate || 0);
                  const lineTax = line * (parseFloat(it.tax_rate || 0) / 100);
                  return (
                    <tr key={i}>
                      <td>
                        <select value={it.product_id} onChange={(e) => updatePurchaseItem(i, 'product_id', e.target.value)}>
                          <option value="">-- Select --</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.unit})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={it.quantity}
                          onChange={(e) => updatePurchaseItem(i, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={it.rate}
                          onChange={(e) => updatePurchaseItem(i, 'rate', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={it.tax_rate}
                          onChange={(e) => updatePurchaseItem(i, 'tax_rate', e.target.value)}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(line + lineTax)}</td>
                      <td>
                        <button type="button" className="btn-icon" onClick={() => removePurchaseItem(i)}>
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button type="button" className="btn btn-sm btn-gray" style={{ marginTop: 8 }} onClick={addPurchaseItem}>
              <Plus size={12} /> Add Item
            </button>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <div className="form-group">
                <label>Discount</label>
                <input
                  type="number"
                  value={purchaseForm.discount}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, discount: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Transport Charges</label>
                <input
                  type="number"
                  value={purchaseForm.transport_charges}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, transport_charges: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Paid Amount</label>
                <input
                  type="number"
                  value={purchaseForm.paid_amount}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, paid_amount: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Payment Mode</label>
                <select
                  value={purchaseForm.payment_mode}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, payment_mode: e.target.value })}
                >
                  <option value="credit">Credit</option>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  value={purchaseForm.notes}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, notes: e.target.value })}
                />
              </div>
            </div>

            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Subtotal:</span>
                <strong>{formatCurrency(purchaseTotals.sub)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Tax:</span>
                <strong>{formatCurrency(purchaseTotals.tax)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Transport:</span>
                <strong>{formatCurrency(purchaseForm.transport_charges || 0)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Discount:</span>
                <strong>-{formatCurrency(purchaseForm.discount || 0)}</strong>
              </div>
              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #cbd5e1' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: '#1e40af' }}>
                <strong>Grand Total:</strong>
                <strong>{formatCurrency(purchaseTotals.total)}</strong>
              </div>
            </div>
          </div>

          {/* Sale */}
          <div style={{ opacity: includeSale ? 1 : 0.45, pointerEvents: includeSale ? 'auto' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h4 style={{ margin: '6px 0', fontSize: 13, color: '#059669' }}>Sale</h4>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Total: <strong>{formatCurrency(saleTotals.total)}</strong>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Ledger (Customer) *</label>
                <select
                  value={saleForm.ledger_id}
                  onChange={(e) => {
                    const c = customerLedgers.find((x) => x.id == e.target.value);
                    setSaleForm({
                      ...saleForm,
                      ledger_id: e.target.value,
                      delivery_address: c?.address || '',
                    });
                  }}
                >
                  <option value="">-- Select --</option>
                  {customerLedgers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={saleForm.sale_date}
                  onChange={(e) => setSaleForm({ ...saleForm, sale_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Delivery Vehicle</label>
                <input
                  value={saleForm.vehicle_text}
                  onChange={(e) => setSaleForm({ ...saleForm, vehicle_text: e.target.value })}
                  placeholder="Type vehicle number (optional)"
                />
              </div>
              <div className="form-group full-width">
                <label>Delivery Address</label>
                <textarea
                  value={saleForm.delivery_address}
                  onChange={(e) => setSaleForm({ ...saleForm, delivery_address: e.target.value })}
                />
              </div>
            </div>

            <h4 style={{ margin: '12px 0 8px', fontSize: 13, color: '#475569' }}>Items</h4>
            <table className="items-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Product</th>
                  <th>Available</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Tax%</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {saleForm.items.map((it, i) => {
                  const line = parseFloat(it.quantity || 0) * parseFloat(it.rate || 0);
                  const lineTax = line * (parseFloat(it.tax_rate || 0) / 100);
                  const prod = products.find((x) => x.id == it.product_id);
                  return (
                    <tr key={i}>
                      <td>
                        <select value={it.product_id} onChange={(e) => updateSaleItem(i, 'product_id', e.target.value)}>
                          <option value="">-- Select --</option>
                          {saleProductOptions.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.unit})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {prod ? <span>{prod.stock_qty}</span> : '-'}
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={it.quantity}
                          onChange={(e) => updateSaleItem(i, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={it.rate}
                          onChange={(e) => updateSaleItem(i, 'rate', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={it.tax_rate}
                          onChange={(e) => updateSaleItem(i, 'tax_rate', e.target.value)}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(line + lineTax)}</td>
                      <td>
                        <span style={{ color: '#94a3b8' }}>—</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
              Sales items can only be selected from the items added in the Purchase section.
            </div>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <div className="form-group">
                <label>Discount</label>
                <input
                  type="number"
                  value={saleForm.discount}
                  onChange={(e) => setSaleForm({ ...saleForm, discount: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Transport Charges</label>
                <input
                  type="number"
                  value={saleForm.transport_charges}
                  onChange={(e) => setSaleForm({ ...saleForm, transport_charges: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Paid Amount</label>
                <input
                  type="number"
                  value={saleForm.paid_amount}
                  onChange={(e) => setSaleForm({ ...saleForm, paid_amount: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Payment Mode</label>
                <select
                  value={saleForm.payment_mode}
                  onChange={(e) => setSaleForm({ ...saleForm, payment_mode: e.target.value })}
                >
                  <option value="credit">Credit</option>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div className="form-group">
                <label>Delivery Status</label>
                <select
                  value={saleForm.delivery_status}
                  onChange={(e) => setSaleForm({ ...saleForm, delivery_status: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea value={saleForm.notes} onChange={(e) => setSaleForm({ ...saleForm, notes: e.target.value })} />
              </div>
            </div>

            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Subtotal:</span>
                <strong>{formatCurrency(saleTotals.sub)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Tax:</span>
                <strong>{formatCurrency(saleTotals.tax)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Transport:</span>
                <strong>{formatCurrency(saleForm.transport_charges || 0)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Discount:</span>
                <strong>-{formatCurrency(saleForm.discount || 0)}</strong>
              </div>
              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #cbd5e1' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: '#059669' }}>
                <strong>Grand Total:</strong>
                <strong>{formatCurrency(saleTotals.total)}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

